import { FC, useMemo, useState, useEffect } from "react";
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import { convertDate } from "@funded-here-interface/common/src/Utils/date";
import {
  VaAccountTab,
  VirtualAccount,
} from "../../../services/getVirtualAccounts";
import useGetVirtualAccounts from "../../../hooks/useGetVirtualAccounts";

import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { toast } from "react-toastify";
import { Box, Button } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import useDownloadVirtualAccountsCSV from "../../../hooks/useDownloadVirtualAccountsCSV";
import { downloadFile } from "@funded-here-interface/common/src/Utils/file";
import { FileType } from "@funded-here-interface/common/src/constant/enum";

const CurrentBalance: FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const useDownloadVirtualAccountsCSVMutation = useDownloadVirtualAccountsCSV();
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [filter, setFilter] = useState<MRT_ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const sortBy =
    sorting.length > 0
      ? {
          field: sorting[0].id,
          order: sorting[0].desc ? ("desc" as const) : ("asc" as const),
        }
      : undefined;

  // Call hook at top-level (not inside a nested function) to satisfy rules-of-hooks
  const { data, error, isLoading } = useGetVirtualAccounts({
    token,
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    sortBy,
    globalFilter: globalFilter,
    filter,
    tab: VaAccountTab.CURRENT_BALANCE,
  });

  // show errors via toast
  useEffect(() => {
    if (error) toast.error((error as Error).message);
  }, [error]);

  const columns = useMemo<MRT_ColumnDef<VirtualAccount>[]>(
    () => [
      {
        accessorKey: "type",
        header: "AccountType",
      },
      {
        accessorKey: "accountId",
        header: "Virtual Account ID",
        Cell: ({ cell }) => <p>{String(cell.getValue() ?? "-")}</p>,
      },
      {
        accessorKey: "bankAccountId",
        header: "Bank Account Number",
        Cell: ({ cell }) => <p>{String(cell.getValue() ?? "-")}</p>,
      },
      {
        accessorKey: "bankAccountHolder",
        header: "Bank Account Name",
        Cell: ({ cell }) => <p>{String(cell.getValue() ?? "-")}</p>,
      },
      {
        accessorKey: "bankName",
        header: "Bank Name",
        Cell: ({ cell }) => <p>{String(cell.getValue() ?? "-")}</p>,
      },
      {
        accessorKey: "bicSwift",
        header: "SWIFT Code",
        Cell: ({ cell }) => <p>{String(cell.getValue() ?? "-")}</p>,
      },
      {
        accessorKey: "ref",
        header: "Reference",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "createdDate",
        header: "Date Created",
        enableColumnFilter: false,
        Cell: ({ cell }) => <p>{convertDate(cell.getValue<string>())}</p>,
      },
      {
        accessorKey: "isActive",
        header: "Account Status",
        Cell: ({ cell }) => {
          const value = cell.getValue<boolean>();
          return <p>{value === true ? "active" : "inactive"}</p>;
        },
      },
      {
        accessorKey: "currency",
        header: "Currency",
      },
      {
        accessorKey: "balance",
        header: "Balance",
      },
    ],
    []
  );

  const table = useMantineReactTable({
    enableColumnActions: false,
    enableColumnFilters: true,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    paginationDisplayMode: "pages",
    state: {
      pagination,
      sorting,
      globalFilter,
      isLoading,
      showProgressBars: isLoading,
      columnFilters: filter,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setFilter,
    columns,
    initialState: { density: "xs" },
    mantineTableProps: {
      withBorder: true,
    },
    rowCount: data?.totalRow ?? 0,
    data: data?.vaAccounts ?? [],
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      >
        <Button
          color="lightblue"
          //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
          onClick={handleExportData}
          leftIcon={<IconDownload />}
          variant="filled"
        >
          Export All Data
        </Button>
      </Box>
    ),
  });

  const handleExportData = () => {
    useDownloadVirtualAccountsCSVMutation.mutate(
      { token },
      {
        onSuccess: (data: Blob) => {
          downloadFile(data, "Virtual Accounts", FileType.CSV);
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  return (
    <div>
      <MantineReactTable table={table} />
    </div>
  );
};

export default CurrentBalance;
